import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { dateTimeText, utcText } from '../../../../shared/date/local-time'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { serverMessage } from '../../../../shared/error/server-message'
import { recycleWrite } from './ai-tools-api'
import { UNLINKED_NOTE, kindNoun, personPage, personText } from './ai-tools-words'
import { DeleteDialog, movedText } from './delete-dialog'

// What the detail pages (a session's, a postmortem's) both draw in their
// <dl sw-dl> lists.

// A time in the viewer's zone, the exact UTC instant on hover.
export const When = ({ value }) => (value ? <time dateTime={new Date(value).toISOString()} title={utcText(value)}>{dateTimeText(value)}</time> : null)

// A term and its value; a value that is not there leaves no empty row.
export const Row = ({ label, children }) => (children === null || children === undefined || children === '' ? null : <><dt>{label}</dt><dd>{children}</dd></>)

// Who a row is linked to when it is linked to no one (UNLINKED_NOTE says why).
export const UnlinkedNote = () => <><Pill title={UNLINKED_NOTE}>Unlinked</Pill><span className="sw-hint small d-block">{UNLINKED_NOTE}</span></>

// The person a record came from (contract: person), their staff page linked,
// their email under their name; nothing when no one is known (Row then
// leaves no empty row).
export const PersonValue = ({ person }) => {
  if (!person) return null
  const page = personPage(person)
  const name = personText(person)
  return <>{page ? <Link to={page}>{name}</Link> : name}{person.name && person.email ? <span className="sw-hint small d-block">{person.email}</span> : null}</>
}

// A detail page's own writes to its record (R2): Delete (the recycle bin's
// confirm, with what goes with it) and Restore. useRecordWrites({ kind,
// onChanged }) -> { askDelete(id), restore(id), busy, dialog, notice }:
// onChanged() reads the page again after either (it then shows, or stops
// showing, that it is in the recycle bin); notice says what happened.
export const useRecordWrites = ({ kind, onChanged }) => {
  const [deleting, setDeleting] = useState(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState(null) // { tone, text, failure }
  const noun = kindNoun(kind)

  const askDelete = id => { setSaid(null); setDeleting({ selection: { ids: [id] } }) }
  const restore = async id => {
    if (busy) return
    setBusy(true)
    setSaid(null)
    try {
      const answer = await recycleWrite('restore', kind, { ids: [id] })
      setSaid(answer && answer.changed
        ? { tone: 'ok', text: `Restored this ${noun} from the recycle bin.` }
        : { tone: 'warn', text: `Nothing was restored: this ${noun} isn't in the recycle bin on its own.` })
    } catch (err) {
      setSaid({ tone: 'bad', text: serverMessage(err, `This ${noun} couldn't be restored.`), failure: err })
    }
    setBusy(false)
    onChanged()
  }
  const moved = answer => {
    setDeleting(null)
    setSaid({ tone: 'ok', text: movedText(kind, answer) })
    onChanged()
  }

  const dialog = deleting && <DeleteDialog kind={kind} selection={deleting.selection} onCancel={() => setDeleting(null)} onDone={moved} />
  const notice = said && <Notice tone={said.tone} role={said.tone === 'bad' ? 'alert' : 'status'}>{said.text}{said.failure && <ErrorReference error={said.failure} />}</Notice>
  return { askDelete, restore, busy, dialog, notice }
}

// A binned record's page says so first (it still opens: the recycle bin
// links to it): when it was moved there and by whom. Restore is the page
// header's (its menu), not repeated here.
export const BinnedNotice = ({ row, kind }) =>
  <Notice tone="warn" role="status">
    <strong>In the recycle bin.</strong>{' '}
    {`This ${kindNoun(kind)} was moved there ${row.deletedText || dateTimeText(row.deletedAt)}${row.deletedByText ? ` by ${row.deletedByText}` : ''}.`}
  </Notice>
